import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { getApiBaseUrl } from "./api/services/utils/apiConfig";
import { setFavicon } from "./utils/favicon"; // 파비콘 설정 유틸리티 추가
import "./App.css"; // 전역 폰트 설정

// 무거운 컴포넌트들을 lazy로 변경
const Home = lazy(() => import("./pages/home/Home"));
const AdminLayout = lazy(() => import("./components/admin/layout/AdminLayout"));
const Post = lazy(() => import('./pages/post/Post'));
const BusinessPage = lazy(() => import("./pages/media/business/BusinessPage"));
const BusinessDetail = lazy(() => import('./pages/detail/business/BusinessDetail'));
const NewsPage = lazy(() => import("./pages/media/news/NewsPage"));
const NewsDetail = lazy(() => import('./pages/detail/news/NewsDetail'));
const IssuePage = lazy(() => import("./pages/media/issue/IssuePage"));
const IssueDetail = lazy(() => import('./pages/detail/issue/IssueDetail'));
const NoticePage = lazy(() => import("./pages/media/notice/NoticePage"));
const NoticeDetail = lazy(() => import('./pages/detail/notice/NoticeDetail'));
const CommunityAllPage = lazy(() => import("./pages/media/community/CommunityAllPage"));

// 가벼운 컴포넌트들은 그대로 유지
import BusinessField from "./components/common/businessInfo/other/businnesfield/BusinessField";
import BusinessTransportPage from "./components/common/businessInfo/other/businnesfield/BusinessTransportPage";
import BusinessFinancePage from "./components/common/businessInfo/other/businnesfield/BusinessFinancePage";
import BusinessShoppingmallPage from "./components/common/businessInfo/other/businnesfield/BusinessShoppingmallPage";
import BusinessProcessPage from "./components/common/businessInfo/other/businnesfield/BusinessProcessPage";
import Introduction from "./pages/introduction/Introduction";
import PopupManager from "./components/common/popupmanager/PopupManager";
import AdminLogin from './components/admin/auth/login/AdminLogin';
import ProtectedRoute from './components/admin/auth/ProtectedRoute';
import Terms from './pages/terms/Terms';
import Privacy from './pages/privacy/Privacy';
import MainHeader from "./components/header/mainheader/MainHeader";
import JobApply from './pages/detail/jobapply/JobApply';
import FloatingContactButton from './components/common/floatingcontactbtn/FloatingContactButton';
import AboutUs from './components/common/businessInfo/other/aboutus/aboutus';
import Values from './components/common/businessInfo/other/values/values';
import Location from './components/common/businessInfo/other/location/Location';
import { useParams } from 'react-router-dom';


// JobApply 라우트 분기용 래퍼
function JobApplyRouteWrapper() {
  const { id } = useParams();
  const [job, setJob] = React.useState(null);
  
  React.useEffect(() => {
    if (!id) return;
    fetch(`${getApiBaseUrl()}/jobs/${id}`)
      .then(res => res.json())
      .then(data => setJob(data))
      .catch(error => {
        console.error('공고 정보를 가져오는데 실패했습니다:', error);
      });
  }, [id]);
  
  if (!job) return <div>로딩 중...</div>;
  
  return <JobApply />;
}

// ChannelIO 스크립트 로드 함수
const loadChannelIO = () => {
  if (window.ChannelIO) return;
  (function() {
    var w = window;
    if (w.ChannelIO) return;
    function ch(){ch.c(arguments);}
    ch.q=[];
    ch.c=function(args){ch.q.push(args);};
    w.ChannelIO=ch;
    function l(){
      if(w.ChannelIOInitialized){return;}
      w.ChannelIOInitialized = true;
      var s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
      var x = document.getElementsByTagName('script')[0];
      if (x.parentNode) x.parentNode.insertBefore(s, x);
    }
    if(document.readyState === 'complete'){l();}
    else if(window.attachEvent){window.attachEvent('onload',l);}
    else{
      window.addEventListener('DOMContentLoaded',l,false);
      window.addEventListener('load',l,false);
    }
  })();
};

// 경로별 기본 타이틀 (lazy 로딩 전에 즉시 적용)
const PAGE_TITLES = {
  "/": "주식회사 가까운물류",
  "/introd": "회사소개 - 주식회사 가까운물류",
  "/intro/about": "회사소개 - 주식회사 가까운물류",
  "/intro/values": "조직도 - 주식회사 가까운물류",
  "/intro/location": "위치/오시는길 - 주식회사 가까운물류",
  "/business": "사업분야 - 주식회사 가까운물류",
  "/business/transport": "운송사업 - 주식회사 가까운물류",
  "/business/finance": "총무부 - 주식회사 가까운물류",
  "/business/shoppingmall": "쇼핑몰사업 - 주식회사 가까운물류",
  "/business/process": "프로세스 - 주식회사 가까운물류",
  "/community": "커뮤니티 - 주식회사 가까운물류",
  "/news": "화물구직 - 주식회사 가까운물류",
  "/issue": "소식 - 주식회사 가까운물류",
  "/notice": "공지사항 - 주식회사 가까운물류",
  "/careers": "채용공고 - 주식회사 가까운물류",
  "/terms": "이용약관 - 주식회사 가까운물류",
  "/privacy": "개인정보처리방침 - 주식회사 가까운물류",
};

// 일반 사용자 레이아웃
function AppLayout() {
  const location = useLocation();
  const isHome = location.pathname === "/";

  // 경로 변경 시 타이틀 즉시 갱신 (lazy 로딩 지연 방지)
  useEffect(() => {
    const baseTitle = PAGE_TITLES[location.pathname];
    if (baseTitle) {
      document.title = baseTitle;
    }
  }, [location.pathname]);

  // ChannelIO와 FloatingContactButton을 숨길 경로 정의 (공통)
  const disableChannelTalkPaths = [
    /^\/admin/,
    /^\/careers(\/.*)?$/, 
    /^\/privacy$/,
    /^\/terms$/,
  ];

  // ChannelIO 관리 - 지연 로딩으로 최적화
  useEffect(() => {
    const shouldDisableChannelTalk = disableChannelTalkPaths.some(pattern =>
      pattern.test(location.pathname)
    );

    if (shouldDisableChannelTalk) {
      if (window.ChannelIO) window.ChannelIO('shutdown');
    } else {
      // 3초 후에 ChannelIO 로드하여 초기 렌더링 지연 방지
      const timer = setTimeout(() => {
        loadChannelIO();
        // Home 페이지에서는 더 늦게 나타나도록 (추가 2초 지연)
        const delay = location.pathname === '/' ? 2000 : 1000;
        setTimeout(() => {
          if (window.ChannelIO) {
            window.ChannelIO('boot', { pluginKey: 'e2ac9167-3a4f-4ef6-baa8-ee520f386bcd' });
          }
        }, delay);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // 메인 헤더를 숨길 경로 정의
  const hideMainHeaderPaths = [
    /^\/careers(\/\d+)?$/,  
    /^\/careers\/(\d+)\/apply$/,  
    /^\/privacy$/,
    /^\/terms$/,
  ];
  const shouldHideMainHeader = hideMainHeaderPaths.some(pattern => pattern.test(location.pathname));

  // 푸터를 숨길 경로 정의
  const hideFooterPaths = [
    /^\/careers(\/\d+)?$/,  
    /^\/careers\/(\d+)\/apply$/,  
    /^\/privacy$/,
    /^\/terms$/,
  ];

  // FloatingContactButton 숨기기
  const shouldHideFloatingButton = disableChannelTalkPaths.some(pattern =>
    pattern.test(location.pathname)
  );

  return (
    <>
      {/* 헤더 */}
      {/* 홈(/)은 Home에서 레이아웃 컨테이너로 렌더링 */}
      {!isHome &&
        !location.pathname.startsWith('/careers') &&
        !location.pathname.startsWith('/privacy') &&
        !location.pathname.startsWith('/terms') &&
        !shouldHideMainHeader && (
        <MainHeader />
      )}
      <main className="main-content">
        <Suspense fallback={<div className="loading-spinner">로딩중...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/business" element={<BusinessField />} />
            <Route path="/business/transport" element={<BusinessTransportPage />} />
            <Route path="/business/finance" element={<BusinessFinancePage />} />
            <Route path="/business/shoppingmall" element={<BusinessShoppingmallPage />} />
            <Route path="/business/process" element={<BusinessProcessPage />} />
            <Route path="/introd" element={<Introduction />} />
            <Route path="/intro/about" element={<AboutUs />} />
            <Route path="/intro/values" element={<Values />} />
            <Route path="/intro/location" element={<Location />} />
            <Route path="/community" element={<CommunityAllPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:id" element={<NewsDetail />} /> 
            <Route path="/issue" element={<IssuePage />} />
            <Route path="/issue/:id" element={<IssueDetail />} />
            <Route path="/notice" element={<NoticePage />} />
            <Route path="/notice/:id" element={<NoticeDetail />} />
            <Route path="/careers" element={<BusinessPage />} />
            <Route path="/careers/:id" element={<BusinessDetail />} />
            <Route path="/careers/:id/apply" element={<JobApplyRouteWrapper />} />
            <Route path="/post/:id" element={<Post />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
          </Routes>
        </Suspense>
      </main>
      <PopupManager />
      {!shouldHideFloatingButton && <FloatingContactButton />}
    </>
  );
}

// AdminProtectedRoute.js 수정
const AdminProtectedRoute = ({ children }) => {
  const isAdminAuthenticated = () => {
    const adminToken = localStorage.getItem('adminToken');
    const expirationTime = localStorage.getItem('adminTokenExpiration');
    
    if (!adminToken || !expirationTime) {
      return false;
    }
    
    if (new Date().getTime() > parseInt(expirationTime)) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpiration');
      return false;
    }
    return true;
  };

  if (!isAdminAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// 루트 App 컴포넌트
function App() {
  // 앱 시작 시 파비콘 설정
  useEffect(() => {
    setFavicon();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<AdminLogin />} />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div className="loading-spinner">관리자 페이지 로딩중...</div>}>
                  <AdminLayout />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path="/*" element={<AppLayout />} />
        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;
